import { useState, useEffect, useMemo, useRef } from "react";
import {
  TextField,
  Autocomplete,
  Pagination,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getAllTrainings } from "services/api";
import { getTrainings } from "services/api";
import { getCompetencyLevels } from "services/api";
import { getCompetencyToJobs } from "services/api";
import { getCompetencies } from "services/api";
import LoadingScreen from "components/Loading";
import TrainingRow from "./TrainingRow";
import SnackbarWrapper from "components/Snackbar";
import { addTrainingRecord } from "services/api";
import dayjs from "dayjs";
import { deleteTrainingRecordById, exportLearningAndDevelopment } from "services/api";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { CloudDownload } from "@mui/icons-material";
import MDButton from "components/MDButton";
import ImportButton from "components/ImportButton";

// helper to map API rows into objects
const mapRowToObject = (row) => ({
  employeeId: row[0],
  employeeName: row[1],
  jobId: row[2],
  jobName: row[3],
  trainingId: row[4],
  startDate: row[6],
  endDate: row[7],
  expectedOutcomeLevel: row[8],
  competencyId: row[9],
  recordId: row[12],
  achievedLevel: row[13],
});

function LearningAndDevelopment({ canEdit }) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [allComps, setAllComps] = useState([]);
  const [allTrainings, setAllTrainings] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [compToJobs, setCompToJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [jobFilter, setJobFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const snackbarRef = useRef();

  const [page, setPage] = useState(1);
  const pageSize = 5;

  const formatToLocalDateTime = (dateStr) => {
    return new Date(dateStr).toISOString().split(".")[0]; //
  };

  useEffect(() => {
    setPage(1);
  }, [jobFilter, search]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // run all API calls in parallel
        const [trainingsRes, trainingsMaster, levels, compToJobs, comps] = await Promise.all([
          getAllTrainings(), // assigned trainings + employees
          //getAllTrainings: // Gets all employees: for each emp, finds his job and all trainings linked to them
          getTrainings(), // all trainings
          getCompetencyLevels(), // all competency levels
          getCompetencyToJobs(), // job → competency mapping
          getCompetencies(), // all competencies
        ]);

        if (trainingsRes === "error") return;

        // Step 1: Map to objects
        const mapped = trainingsRes.map(mapRowToObject);

        // Step 2: Group by employee
        const grouped = mapped.reduce((acc, item) => {
          if (!acc[item.employeeId]) {
            acc[item.employeeId] = {
              employeeId: item.employeeId,
              employeeName: item.employeeName,
              jobId: item.jobId,
              jobName: item.jobName,
              trainings: [],
            };
          }

          if (item.trainingId) {
            acc[item.employeeId].trainings.push({
              trainingId: item.trainingId,
              trainingName: item.trainingName,
              startDate: item.startDate,
              endDate: item.endDate,
              expectedOutcomeLevel: item.expectedOutcomeLevel,
              competencyId: item.competencyId,
              recordId: item.recordId,
              achievedLevel: item.achievedLevel,
            });
          }

          return acc;
        }, {});

        // ✅ Sort trainings by recordId inside each employee
        Object.values(grouped).forEach((emp) => {
          emp.trainings.sort((a, b) => (a.recordId ?? 0) - (b.recordId ?? 0));
        });

        //  Sort employees by jobId ascending
        const sortedEmployees = Object.values(grouped).sort((a, b) => a.jobId - b.jobId);

        setEmployees(sortedEmployees);

        // set all the master data
        if (trainingsMaster !== "error") setAllTrainings(trainingsMaster);
        if (levels !== "error") setAllLevels(levels);
        if (compToJobs !== "error") setCompToJobs(compToJobs);
        if (comps !== "error") setAllComps(comps);
      } catch (err) {
        console.error("Error fetching data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const handleSave = async (training, employeeId, emp) => {
    try {
      setSavingId(training.recordId);

      // ✅ Required fields check
      if (
        !training?.competencyId ||
        !training?.trainingId ||
        !training?.expectedOutcomeLevel ||
        !training?.startDate ||
        !training?.endDate ||
        !employeeId
      ) {
        openSnack(t("admin.trainings.messages.fieldRequired"), "error");
        setSavingId(null);
        return; // ⛔ exit early
      }

      const payload = {
        id: training?.recordId ?? null,
        trainingId: training.trainingId, //comp To Training Id
        employeeUuid: employeeId,
        startDate: training.startDate ? formatToLocalDateTime(training.startDate) : null,
        endDate: training.endDate ? formatToLocalDateTime(training.endDate) : null,
        expectedOutcomeLevel: training.expectedOutcomeLevel,
        achievedLevel: training?.achievedLevelId ?? null,
      };

      const res = await addTrainingRecord(payload);

      if (res !== "error") {
        onChange(res, emp, training.recordId ? false : true);
      }
      openSnack(t("admin.trainings.messages.addSuccess"), "success");
    } catch (err) {
      openSnack(t("admin.trainings.messages.addFail"), "error");
    } finally {
      setSavingId(null);
    }
  };

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  const handleAchievedChange = async (training, newLevel, employeeId, emp) => {
    // Update local state first
    setSavingId(training.recordId);
    // Build payload for backend
    const payload = {
      id: training.recordId, // record identifier
      trainingId: training.trainingId, // FK
      employeeUuid: employeeId, // from props
      competencyId: training.competencyId, // keep existing
      expectedOutcomeLevel: training.expectedOutcomeLevel,
      achievedLevel: newLevel,
      startDate: training.startDate,
      endDate: training.endDate,
    };

    try {
      const res = await addTrainingRecord(payload); // <- your API call

      // Bubble back to parent with backend response
      onChange(res, emp, false);

      // Optionally show a success snack
      openSnack(t("admin.trainings.messages.achievedUpdateSuccess"), "success");
      setSavingId(null);
    } catch (err) {
      openSnack(t("admin.trainings.messages.achievedUpdateFail"), "error");
      setSavingId(null);
    }
  };

  const handleDelete = async (recordId, emp) => {
    try {
      setSavingId(recordId);
      const res = await deleteTrainingRecordById(recordId);
      if (res !== "error") {
        setEmployees((prev) =>
          prev.map((e) =>
            e.employeeId === emp.employeeId
              ? {
                  ...e,
                  trainings: e.trainings.filter((tr) => tr.recordId !== recordId),
                }
              : e
          )
        );
        openSnack(t("admin.trainings.messages.deleteSuccess"), "success");
      } else {
        openSnack(t("admin.trainings.messages.deleteFail"), "error");
      }
    } catch (err) {
      openSnack(t("admin.trainings.messages.deleteError"), "error");
    } finally {
      setSavingId(null);
    }
  };

  const onChange = (res, emp, isNew) => {
    setEmployees((prev) =>
      prev.map((e) =>
        e.employeeId === emp.employeeId
          ? {
              ...e,
              trainings: isNew
                ? [
                    ...(e.trainings || []),
                    {
                      recordId: res.id,
                      trainingId: res.trainingId,
                      trainingName:
                        allTrainings.find((trg) => trg.id === res.trainingId)?.trainingName || "",
                      startDate: res.startDate,
                      endDate: res.endDate,
                      expectedOutcomeLevel: res.expectedOutcomeLevel,
                      competencyId:
                        res.competencyId ??
                        allTrainings.find((trg) => trg.id === res.trainingId)?.compId ??
                        null,
                      achievedLevel: res.achievedLevel ?? null,
                    },
                  ]
                : e.trainings.map((tr) =>
                    tr.recordId === res.id
                      ? {
                          ...tr,
                          recordId: res.id,
                          trainingId: res.trainingId,
                          trainingName:
                            allTrainings.find((trg) => trg.id === res.trainingId)?.trainingName ||
                            tr.trainingName,
                          startDate: res.startDate,
                          endDate: res.endDate,
                          expectedOutcomeLevel: res.expectedOutcomeLevel,
                          competencyId: res.competencyId ?? tr.competencyId,
                          achievedLevel: res.achievedLevel ?? tr.achievedLevel,
                        }
                      : tr
                  ),
            }
          : e
      )
    );
  };

  const openSnack = (message, type) => {
    snackbarRef.current?.showSnackbar(message, type);
  };

  // unique job names for autocomplete
  const jobOptions = useMemo(() => [...new Set(employees.map((e) => e.jobName))], [employees]);

  // filtering
  const filtered = useMemo(() => {
    let result = employees;

    if (search.trim()) {
      result = result.filter((e) => e.employeeName.toLowerCase().includes(search.toLowerCase()));
    }

    if (jobFilter) {
      result = result.filter((e) => e.jobName === jobFilter);
    }

    return result;
  }, [search, jobFilter, employees]);

  // paging
  const totalPages = useMemo(
    () => Math.ceil(filtered.length / pageSize),
    [filtered.length, pageSize]
  );

  const pagedEmployees = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Search & Filter */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
        <TextField
          label={t("admin.trainings.searchByName")}
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ height: "40px", width: 250 }}
        />

        <Autocomplete
          options={jobOptions}
          value={jobFilter}
          onChange={(e, newValue) => setJobFilter(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.trainings.filterByJob")}
              variant="outlined"
              size="small"
            />
          )}
          sx={{ width: 250 }}
        />

        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <ImportButton
            allowedTypes={["learningAndDevelopment"]}
            onError={handleImportError}
            disabled={!canEdit}
          />

          <MDButton
            variant="contained"
            color="primary"
            disabled={exportLoading}
            startIcon={<CloudDownload />}
            sx={{ marginLeft: "auto" }}
            onClick={async () => {
              setExportLoading(true);
              try {
                const res = await exportLearningAndDevelopment();
                if (res === "error") {
                  console.error("Export failed");
                }
              } catch (e) {
                console.error(e);
              } finally {
                setExportLoading(false);
              }
            }}
          >
            {exportLoading ? <CircularProgress size={20} color="inherit" /> : t("common.export")}
          </MDButton>
        </div>
      </div>

      {/* Employee List */}
      {pagedEmployees.map((emp) => (
        <Card key={emp.employeeId} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">
              {emp.employeeName} — {emp.jobName}
            </Typography>

            {/* Existing trainings */}
            {emp.trainings.length > 0 ? (
              emp.trainings.map((t) => (
                <TrainingRow
                  key={t.recordId}
                  trainingSent={t}
                  jobId={emp.jobId}
                  employeeId={emp.employeeId}
                  allComps={allComps}
                  compToJobs={compToJobs}
                  allTrainings={allTrainings}
                  allLevels={allLevels}
                  openSnack={openSnack}
                  onSave={handleSave}
                  saving={savingId === t.recordId}
                  emp={emp}
                  onAchievedChange={handleAchievedChange}
                  onDelete={handleDelete}
                  canEdit={canEdit}
                />
              ))
            ) : (
              <Typography color="text.secondary">{t("admin.trainings.noTrainings")}</Typography>
            )}

            {/* Add Training Section (always shown) */}
            {canEdit && (
              <>
                {" "}
                <Typography
                  variant="subtitle1"
                  sx={{ mt: 3, mb: 1, fontWeight: "bold", color: "text.primary" }}
                >
                  {t("admin.trainings.addTraining")}
                </Typography>
                <TrainingRow
                  trainingSent={null} // empty row for adding
                  jobId={emp.jobId}
                  employeeId={emp.employeeId}
                  allComps={allComps}
                  compToJobs={compToJobs}
                  allTrainings={allTrainings}
                  allLevels={allLevels}
                  openSnack={openSnack}
                  onSave={handleSave}
                  emp={emp}
                  canEdit={canEdit}
                />{" "}
              </>
            )}
          </CardContent>
          <SnackbarWrapper
            ref={snackbarRef}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          />
        </Card>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          sx={{ mt: 3 }}
        />
      )}
    </div>
  );
}

LearningAndDevelopment.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default LearningAndDevelopment;
